import type { WorkflowRequest } from "./workflowTypes";

import { addNotification } from "../notifications/notificationStore";
import type { AppNotification } from "../notifications/types";

// DEV-932 Phase 6: ALL Data/Security flatfile imports removed. Reads
// were used to compute "before" role/membership state for workflow side
// effects; with the flatfile gone, those reads default to empty (the
// "before" defaults to reader / inactive) and the workflow's own
// payload supplies "after" explicitly. Writes go to the API directly.
//
// The effective-role helpers below now return the conservative default
// when no caller-supplied data is available. Callers that need accurate
// "before" state should pass it in (future enhancement) or rely on the
// workflow payload's old_role / old_is_member fields, which are already
// populated by the request creators in CommunitySettingsModal and
// ProjectConfigModal.
import type { ProjectRole, CommunityRole } from "../types/security";
import { respondToRoleInvitation } from "../api/chat";
import { setKpiProgressEntryStatus } from "../Data/KpiProgress/progressEntries";
import {
  upsertCommunityMembership as apiUpsertCommunityMembership,
  upsertCommunityRole as apiUpsertCommunityRole,
  deleteCommunityRole as apiDeleteCommunityRole,
  createProjectAclEntry as apiCreateProjectAclEntry,
  deactivateProjectAclEntry as apiDeactivateProjectAclEntry,
  fetchProjectAclByProject as apiFetchProjectAclByProject,
} from "../api/security";
import { invalidateByPrefix } from "../api/queryCache";

export type ApplySideEffectsResult = {
  ok: boolean;
  error?: string;
};

function normRole(v: any): ProjectRole {
  const s = String(v || "viewer") as any;
  if (s === "owner_admin" || s === "contributor" || s === "viewer") return s;
  return "viewer";
}

/**
 * DEV-1022 — Map a V2 Permission Set bundle name to its closest V1
 * community role for the dual-write bridge. Bundles seeded by migration
 * 130 use predictable naming ("Community Admin", "Community Owner",
 * "Rotary Org Admin", "Community Content Creator", etc.) so a substring
 * heuristic produces stable mappings without an extra API call.
 *
 * Bridge code — remove when DEV-1000 migrates the V1 reads to V2.
 */
function bundleNameToCommunityRoleV1(bundleName: string): CommunityRole {
  const n = String(bundleName || "").toLowerCase();
  if (n.includes("owner")) return "owner";
  if (n.includes("admin")) return "admin";
  if (n.includes("curator") || n.includes("creator")) return "content_creator";
  if (n.includes("contributor")) return "content_contributor";
  return "reader";
}

function normCommunityRole(v: any): CommunityRole {
  const s = String(v || "reader") as any;
  // Canonical community roles live in src/Data/Security/communityRoles.ts
  if (
    s === "owner" ||
    s === "admin" ||
    s === "content_creator" ||
    s === "content_contributor" ||
    s === "reader"
  ) return s;
  return "reader";
}


// DEV-932 Phase 6: these helpers used flatfile + localStorage data to
// compute "before" state. With the flatfile sweep, the reliable source
// of "before" is the workflow request's payload (old_role,
// old_is_member) which is set when the workflow is created. Callers
// downstream now use the payload directly. These helpers stay as
// conservative-default no-ops to keep call sites compiling, but they
// always return reader / inactive — DO NOT trust their results for new
// logic.
function getEffectiveCommunityRole(_userGuid: string, _communityGuid: string): CommunityRole {
  return "reader";
}

function getEffectiveCommunityMembershipActive(_userGuid: string, _communityGuid: string): boolean {
  return false;
}

/**
 * Push notifications to both the requestor and the assignee(s) when a workflow resolves.
 *
 * Vocabulary depends on the workflow's action_type:
 *   - Invitation workflows (community.role_assignment, project.role_assignment)
 *     use the invite/accept model: "accepted / declined / cancelled".
 *   - Other workflows keep the V1 approval model: "approved / rejected / cancelled".
 *
 * For invitation workflows, the requestor notification is suppressed locally —
 * the backend's AcceptGrant / DeclineGrant handlers write server-side
 * notifications (DEV-1023) that surface in the granter's bell via the
 * notification bridge. Emitting locally too would duplicate.
 */
function emitResolutionNotifications(w: WorkflowRequest, resolvedBy: string, outcome: "approved" | "rejected" | "cancelled"): void {
  const now = new Date().toISOString();
  const title = w.title || w.action_type;

  // DEV-1023 — pick invite-vs-approval vocabulary by workflow type.
  const isInvitation =
    w.action_type === "community.role_assignment" ||
    w.action_type === "project.role_assignment" ||
    // DEV-1193 Phase 4: slot-anchored community invitation uses the
    // same accepted/declined vocabulary as the other invitation types.
    w.action_type === "community.slot_role_assignment";

  let resolverVerb: string;
  let requestorVerb: string;
  if (isInvitation) {
    if (outcome === "approved") {
      resolverVerb = "accepted";
      requestorVerb = "accepted";
    } else if (outcome === "rejected") {
      resolverVerb = "declined";
      requestorVerb = "declined";
    } else {
      resolverVerb = "cancelled";
      requestorVerb = "cancelled";
    }
  } else {
    resolverVerb = outcome;
    requestorVerb = outcome;
  }

  const resolverLabel = resolverVerb.charAt(0).toUpperCase() + resolverVerb.slice(1);
  const requestorLabel = requestorVerb.charAt(0).toUpperCase() + requestorVerb.slice(1);

  // Notify the requestor (created_by). For invitation workflows the backend
  // writes the server-side bell notification (DEV-1023); suppress the
  // duplicate local one. For other workflows (project.permissions_change,
  // content.* approvals, etc.) the local notification is still the only
  // signal the requestor gets.
  if (!isInvitation && w.created_by && w.created_by !== resolvedBy) {
    const notif: AppNotification = {
      id: `wf_${w.workflow_guid}_${outcome}_requestor`,
      type: "approval_needed",
      title: `${requestorLabel}: ${title}`,
      body: `Your request was ${requestorVerb}.`,
      timestamp: now,
      isRead: false,
      href: "/actions",
      dedupeKey: `workflow:${w.workflow_guid}:${outcome}:requestor`,
    };
    addNotification(notif);
  }

  // Notify the resolver — confirmation of their action.
  {
    const notif: AppNotification = {
      id: `wf_${w.workflow_guid}_${outcome}_resolver`,
      type: "approval_needed",
      title: `You ${resolverVerb}: ${title}`,
      timestamp: now,
      isRead: false,
      href: "/actions",
      dedupeKey: `workflow:${w.workflow_guid}:${outcome}:resolver`,
    };
    addNotification(notif);
  }
}

export function applyApprovedWorkflowSideEffects(w: WorkflowRequest, approvedBy: string): ApplySideEffectsResult {
  if (!w || w.status !== "approved") return { ok: true };

  // Emit notifications to requestor and resolver
  emitResolutionNotifications(w, approvedBy, "approved");

  // ── Role assignment invitation (Accept) ──
  // When the invitee approves a role_assignment workflow in the Action Center,
  // call the backend respond endpoint to activate the assignment.
  //
  // Two flavors share this action_type:
  //   (a) Project role-SLOT invitations (RoleAssignmentModal) — payload has
  //       `assignment_guid` pointing at a project_role_assignments row.
  //       Accept via /api/role-invitations/{guid}/respond.
  //   (b) Project governance grants (ProjectOwnersAdminsPanel / "Direct
  //       Grants" tab) — payload has `grant_guid` pointing at a
  //       permission_grants row. Accept via POST /grants/{guid}/accept.
  //
  // The two share action_type for historical reasons (DEV-1039 used the
  // same emitter pattern). Disambiguate by payload shape — grant_guid
  // takes precedence since the governance flow was the bug reporter.
  if (w.action_type === "project.role_assignment") {
    const projectGrantGuid = String((w.payload as any)?.grant_guid || "");
    if (projectGrantGuid) {
      // Governance grant accept path.
      import("../security/v2/apiClient")
        .then(({ permissionsApiV2 }) => permissionsApiV2.acceptGrant(projectGrantGuid))
        .then(() => console.info("[workflowSideEffects] Project grant accepted:", projectGrantGuid))
        .catch((err) => console.warn("[workflowSideEffects] Project grant accept failed:", err?.message || err));
      return { ok: true };
    }
    const assignmentGuid = String((w.payload as any)?.assignment_guid || "");
    if (!assignmentGuid) {
      return { ok: false, error: "Missing assignment_guid or grant_guid in workflow payload." };
    }
    respondToRoleInvitation(assignmentGuid, "accept")
      .then(() => console.info("[workflowSideEffects] Role invitation accepted:", assignmentGuid))
      .catch((err) => console.warn("[workflowSideEffects] Role invitation accept failed:", err?.message || err));
    return { ok: true };
  }

  // DEV-1088. Mini-Project role-assignment invitation (Accept).
  // Distinct action_type from the Full Project flow so we can route to
  // the Mini-Project-specific respond endpoint. The endpoint flips the
  // assignment to 'active', auto-adds the user to the team roster as
  // 'member' if not already there, and notifies the inviter.
  if (w.action_type === "mini_project.role_assignment") {
    const assignmentGuid = String((w.payload as any)?.assignment_guid || "");
    if (!assignmentGuid) {
      return { ok: false, error: "Missing assignment_guid in workflow payload." };
    }
    // The accepted assignment causes the backend to auto-add the user
    // to mini_project_team_members (POLICY_TEAM_ROSTERS §3.9). The
    // About-tab TeamSection listens for `l4e_miniproject_team_persisted`
    // — dispatch it after the API confirms acceptance so the invitee's
    // own session re-renders with their new team card. Other sessions
    // (Lead's browser) pick the change up via the SignalR
    // SecurityUpdated → team_persisted dispatch in signalrConnection.ts.
    const miniProjectGuid = String(
      (w.payload as any)?.mini_project_guid || w.target_guid || ""
    );
    // DEV-1089 Phase A. Pull the invitee's reply-note (stamped onto
    // the terminal history entry by onApprove in ContentApprovalsPage)
    // and forward it to the respond endpoint as the `note` field, so
    // the Lead's ack notification surfaces the assignee's reply text.
    const histList = Array.isArray(w.history) ? w.history : [];
    const lastEntry = histList.length > 0 ? histList[histList.length - 1] : null;
    const acceptNote = lastEntry?.action === "approve" ? String(lastEntry.note || "") : "";
    import("../api/miniProjectRoles")
      .then(({ respondToMiniProjectRoleInvitation }) =>
        respondToMiniProjectRoleInvitation(assignmentGuid, {
          action: "accept",
          note: acceptNote || null,
        })
      )
      .then(() => {
        console.info("[workflowSideEffects] Mini-Project role invitation accepted:", assignmentGuid);
        if (miniProjectGuid) {
          window.dispatchEvent(
            new CustomEvent("l4e_miniproject_team_persisted", {
              detail: { miniProjectGuid, source: "role-acceptance" },
            }),
          );
          window.dispatchEvent(new Event("l4e_miniproject_roles_changed"));
        }
      })
      .catch((err) => console.warn("[workflowSideEffects] Mini-Project role invitation accept failed:", err?.message || err));
    return { ok: true };
  }

  // DEV-1193 Phase 4. Slot-anchored community role invitation (Accept).
  // Distinct from community.role_assignment (V1 permission-tier model);
  // this routes to the new CommunityRoleSlotInvitationsController.
  // On accept: the backend flips assignment to 'active', sets
  // slot.LinkedUserGuid = invitee, fires CommunitySlotOccupancyService
  // (rhythm reassignment + Lead cascade), and notifies the inviter.
  if (w.action_type === "community.slot_role_assignment") {
    const assignmentGuid = String((w.payload as any)?.assignment_guid || "");
    if (!assignmentGuid) {
      return { ok: false, error: "Missing assignment_guid in workflow payload." };
    }
    const histList = Array.isArray(w.history) ? w.history : [];
    const lastEntry = histList.length > 0 ? histList[histList.length - 1] : null;
    const acceptNote = lastEntry?.action === "approve" ? String(lastEntry.note || "") : "";
    import("../api/communityRoleSlotInvitations")
      .then(({ respondToCommunityRoleSlotInvitation }) =>
        respondToCommunityRoleSlotInvitation(assignmentGuid, {
          action: "accept",
          note: acceptNote || null,
        }),
      )
      .then(() => {
        console.info("[workflowSideEffects] Community slot invitation accepted:", assignmentGuid);
        // DEV-1368 fix — the accepted slot role lands in the user's slot
        // occupancies, but nothing was refreshing them, so the new role
        // (e.g. Rotary International) didn't appear in the Action Center rail
        // until a full reload. Invalidate the occupancies cache (fires the
        // keyed cache-update event the rail listens to) and nudge the V1
        // community-roles source for good measure.
        invalidateByPrefix("communitySlotOccupancies:");
        // DEV-1368 — re-joining a previously-held slot must clear its ghost
        // "Former" entry from the previous-roles cache.
        invalidateByPrefix("communityPreviousRoles:");
        window.dispatchEvent(
          new CustomEvent("l4e_security_updated", { detail: { source: "community-slot-accept" } }),
        );
      })
      .catch((err) =>
        console.warn(
          "[workflowSideEffects] Community slot invitation accept failed:",
          err?.message || err,
        ),
      );
    return { ok: true };
  }

  // DEV-892. Community role-assignment invitation (Accept).
  // When the recipient accepts a community.role_assignment workflow, flip the
  // pending permission grant to active via the v2 RBAC API. The grant_guid is
  // carried in the workflow payload by the producer (CommunityAudienceTab).
  if (w.action_type === "community.role_assignment") {
    const grantGuid = String((w.payload as any)?.grant_guid || "");
    if (!grantGuid) {
      return { ok: false, error: "Missing grant_guid in workflow payload." };
    }
    // Lazy import keeps the workflow module out of the v2 RBAC bundle when not used
    import("../security/v2/apiClient")
      .then(({ permissionsApiV2 }) => permissionsApiV2.acceptGrant(grantGuid))
      .then(() => console.info("[workflowSideEffects] Community role grant accepted:", grantGuid))
      .catch((err) => console.warn("[workflowSideEffects] Community role grant accept failed:", err?.message || err));

    // DEV-1022 — V1 dual-write bridge. The legacy V1 reads in
    // MyCommunitiesCardsPage (Lead → Communities), EngagementsPage
    // (Contribute → Engagements), and CommunitiesPage (Explore →
    // Communities) all source from community_role_assignments and
    // community_memberships. Accepting a V2 grant alone leaves those
    // surfaces empty, so we dual-write a V1 row here. Bridge code —
    // remove in the Phase 6 cutover (DEV-1000) once all consumer
    // surfaces read directly from V2 grants.
    const targetUserGuidForBridge = String((w.payload as any)?.user_guid || "");
    const targetCommunityGuidForBridge = String((w.payload as any)?.community_guid || "");
    const bundleName = String((w.payload as any)?.role || "");
    if (targetUserGuidForBridge && targetCommunityGuidForBridge) {
      const v1Role = bundleNameToCommunityRoleV1(bundleName);
      // DEV-1368 fix — accepting a V1 community role grant dispatched no
      // refresh, so the new role didn't appear in the Action Center rail
      // until a full reload. Dual-write the V1 bridge rows, THEN nudge the
      // role sources (and occupancies, in case it's slot-backed too).
      Promise.all([
        apiUpsertCommunityRole({
          userGuid: targetUserGuidForBridge,
          communityEntityGuid: targetCommunityGuidForBridge,
          role: v1Role,
          assignedByUserGuid: approvedBy || undefined,
        }).catch((err) =>
          console.warn("[workflowSideEffects] DEV-1022 V1 role dual-write failed:", err?.message || err),
        ),
        apiUpsertCommunityMembership({
          userGuid: targetUserGuidForBridge,
          communityEntityGuid: targetCommunityGuidForBridge,
          membershipType: "joined",
          status: "active",
        }).catch((err) =>
          console.warn("[workflowSideEffects] DEV-1022 V1 membership dual-write failed:", err?.message || err),
        ),
      ]).then(() => {
        invalidateByPrefix("communitySlotOccupancies:");
        // DEV-1368 — keep the previous-roles cache consistent on accept.
        invalidateByPrefix("communityPreviousRoles:");
        window.dispatchEvent(
          new CustomEvent("l4e_security_updated", { detail: { source: "community-role-accept" } }),
        );
      });
    }
    return { ok: true };
  }

  if (w.action_type === "project.permissions_change") {
    const projectGuid = String((w.payload as any)?.project_guid || w.target_guid || "");
    const targetUserGuid = String((w.payload as any)?.target_user_guid || (w.payload as any)?.user_guid || "");
    const requested = (w.payload as any)?.new_role;
    const op = String((w.payload as any)?.operation || "set");

    if (!projectGuid || !targetUserGuid) {
      return { ok: false, error: "Missing project/user identifiers." };
    }

    // DEV-932 Phase 6: localStorage ACL reads/writes removed.
    // - The "last admin" pre-check used to read the ACL synchronously
    //   from localStorage; since the API path is async, that check is
    //   relaxed here. The same guard belongs server-side in the new
    //   ReplaceProjectAcl / Create/Deactivate endpoints — file as
    //   follow-up if not already enforced. Workflow payloads carry
    //   old_role / new_role explicitly, so the typical case is covered.
    // - Writes go to the API (createProjectAclEntry / deactivateProjectAclEntry).
    //   Fire-and-forget — same pattern the community branch below uses.
    const isRemove = op === "remove" || String(requested) === "remove";
    const newRole = isRemove ? null : normRole(requested);

    if (isRemove) {
      // For deactivate-by-id we'd need the row id; the API also exposes
      // a per-(project, user) lookup via fetchProjectAclByProject. Do an
      // async lookup-then-deactivate. Fire-and-forget; errors logged.
      apiFetchProjectAclByProject(projectGuid)
        .then((rows) => {
          const target = (rows || []).find(
            (r: any) => String(r.user_guid).toUpperCase() === String(targetUserGuid).toUpperCase()
                        && (r.is_active ?? true) !== false
          );
          if (target?.id) {
            return apiDeactivateProjectAclEntry(String(target.id));
          }
        })
        .catch((err) => console.warn("[workflowSideEffects] API ACL deactivation failed:", err?.message || err));
      return { ok: true };
    }

    apiCreateProjectAclEntry({
      projectGuid: projectGuid as any,
      userGuid: targetUserGuid as any,
      role: newRole!,
      grantedByUserGuid: approvedBy ? (approvedBy as any) : undefined,
    }).catch((err) => console.warn("[workflowSideEffects] API ACL upsert failed:", err?.message || err));
    return { ok: true };
  }

  // DEV-1414 — org membership admission (Approve / Accept).
  // community.membership_request: an admin approved a join request.
  // community.membership_invitation: the invitee accepted.
  // Both resolve the same way: the pending/invited membership row flips to
  // active (the server issues the TU-4 pool grant on activation). One
  // workflow source — this handler serves both the org Management →
  // Membership tab and the Action Center inbox (§8.7: two views, one state).
  if (
    w.action_type === "community.membership_request" ||
    w.action_type === "community.membership_invitation"
  ) {
    const userGuid = String((w.payload as any)?.user_guid || "");
    const communityGuid = String((w.payload as any)?.community_guid || w.target_guid || "");
    const membershipType = String((w.payload as any)?.membership_type || "org_member");
    if (!userGuid || !communityGuid) {
      return { ok: false, error: "Missing user/community identifiers." };
    }
    apiUpsertCommunityMembership({
      userGuid,
      communityEntityGuid: communityGuid,
      membershipType,
      status: "active",
    })
      .then(() => {
        window.dispatchEvent(
          new CustomEvent("l4e_security_updated", { detail: { source: "membership-admission" } }),
        );
      })
      .catch((err) =>
        console.warn("[workflowSideEffects] Membership admission upsert failed:", err?.message || err),
      );
    return { ok: true };
  }

  if (w.action_type === "community.permissions_change") {
    const communityGuid = String((w.payload as any)?.community_guid || w.target_guid || "");
    const targetUserGuid = String((w.payload as any)?.target_user_guid || (w.payload as any)?.user_guid || "");
    const requested = (w.payload as any)?.new_role;
    const op = String((w.payload as any)?.operation || "set");
    const desiredIsMember = (w.payload as any)?.new_is_member;

    if (!communityGuid || !targetUserGuid) {
      return { ok: false, error: "Missing community/user identifiers." };
    }

    const beforeRole = getEffectiveCommunityRole(targetUserGuid, communityGuid);
    const beforeIsAdmin = beforeRole === "owner" || beforeRole === "admin";

    const isRemove = op === "remove" || String(requested) === "remove";
    const afterRole = isRemove ? ("reader" as CommunityRole) : normCommunityRole(requested);
    const afterIsAdmin = afterRole === "owner" || afterRole === "admin";

    // DEV-932 Phase 6: "last admin" pre-check used to read flatfile +
    // localStorage data; with both gone, the check is relaxed here. The
    // same guard belongs server-side in upsertCommunityRole. Workflow
    // payloads carry old_role explicitly, so the typical case is
    // covered when the request creator did the check.
    void beforeIsAdmin;
    void afterIsAdmin;

    // Apply role changes (API write-through; localStorage layer removed).
    if (isRemove || afterRole === "reader") {
      apiUpsertCommunityRole({
        userGuid: targetUserGuid,
        communityEntityGuid: communityGuid,
        role: "reader",
        assignedByUserGuid: approvedBy || undefined,
      }).catch((err) => console.warn("[workflowSideEffects] API role deactivation failed:", err?.message || err));
    } else {
      apiUpsertCommunityRole({
        userGuid: targetUserGuid,
        communityEntityGuid: communityGuid,
        role: afterRole,
        assignedByUserGuid: approvedBy || undefined,
      }).catch((err) => console.warn("[workflowSideEffects] API role upsert failed:", err?.message || err));
    }

    // Apply membership changes (API write-through; localStorage gone).
    //
    // Business rule: role > reader ALWAYS implies active membership.
    // For role upserts above reader, force membership active.
    // For removals/downgrades to reader, honor desired_is_member from
    // the workflow payload (old_is_member also available if needed).
    const elevatedRole = !isRemove && afterRole !== "reader";
    const finalIsMember = elevatedRole
      ? true  // Invariant: elevated role → active membership
      : typeof desiredIsMember === "boolean"
        ? !!desiredIsMember
        : true; // Default: stay a member when downgrading to reader.
    const newStatus = finalIsMember ? "active" : "inactive";
    apiUpsertCommunityMembership({
      userGuid: targetUserGuid,
      communityEntityGuid: communityGuid,
      membershipType: "joined",
      status: newStatus,
    }).catch((err) => console.warn("[workflowSideEffects] API membership upsert failed:", err?.message || err));

    return { ok: true };
  }

  if (w.action_type === "project.kpi_progress_update") {
    const entryGuid = String((w.payload as any)?.entry_guid || "");
    if (!entryGuid) return { ok: false, error: "Missing entry_guid in workflow payload." };
    setKpiProgressEntryStatus({ entry_guid: entryGuid, status: "approved", by_user_guid: String(approvedBy || "") });
    return { ok: true };
  }

  // ── Role completion verification (escalated to Community Leader) ──
  // When a Community Leader approves an escalated role completion,
  // the backend controller handles the actual status transition and impact emission.
  // The frontend side-effect here is informational only — the real work is server-side.
  if (w.action_type === "project.role_completion_verification") {
    // No frontend side-effects needed; the backend PATCH endpoint
    // transitions the assignment to 'completed' and emits the impact event
    // when it receives the workflow approval callback.
    console.info(
      "[workflowSideEffects] Role completion verification approved:",
      (w.payload as any)?.assignment_guid,
      "by", approvedBy
    );
    return { ok: true };
  }

  // ── PM trust promotion ──
  // When a Community Leader approves a PM trust promotion,
  // the backend updates pm_trust_levels. No frontend state to update.
  if (w.action_type === "community.pm_trust_promotion") {
    console.info(
      "[workflowSideEffects] PM trust promotion approved:",
      (w.payload as any)?.pm_user_guid,
      "community:", (w.payload as any)?.community_guid,
      "by", approvedBy
    );
    return { ok: true };
  }

  // ── Admin impact grant ──
  // System admin grants bypass workflow (they go direct), but if a workflow
  // is ever used for audit routing, the backend handles impact emission.
  if (w.action_type === "impact.admin_grant") {
    console.info(
      "[workflowSideEffects] Admin impact grant processed:",
      (w.payload as any)?.target_user_guid,
      "dimension:", (w.payload as any)?.target_dimension,
      "by", approvedBy
    );
    return { ok: true };
  }

  return { ok: true };
}

export function applyRejectedWorkflowSideEffects(w: WorkflowRequest, rejectedBy: string): ApplySideEffectsResult {
  if (!w || w.status !== "rejected") return { ok: true };

  // Emit notifications to requestor and resolver
  emitResolutionNotifications(w, rejectedBy, "rejected");

  // ── Role assignment invitation (Decline) ──
  // Two flavors — see applyApprovedWorkflowSideEffects for the same
  // disambiguation: governance grant (grant_guid) vs role slot assignment
  // (assignment_guid). Mirror that here.
  if (w.action_type === "project.role_assignment") {
    const projectGrantGuid = String((w.payload as any)?.grant_guid || "");
    if (projectGrantGuid) {
      // Governance grant decline path — flip permission_grants.status to declined.
      import("../security/v2/apiClient")
        .then(({ permissionsApiV2 }) => permissionsApiV2.declineGrant(projectGrantGuid))
        .then(() => console.info("[workflowSideEffects] Project grant declined:", projectGrantGuid))
        .catch((err) => console.warn("[workflowSideEffects] Project grant decline failed:", err?.message || err));
      return { ok: true };
    }
    const assignmentGuid = String((w.payload as any)?.assignment_guid || "");
    // Extract the decline note from the most recent history entry
    const hist = Array.isArray(w.history) ? w.history : [];
    const lastEntry = hist.length > 0 ? hist[hist.length - 1] : null;
    const declineNote = lastEntry?.action === "reject" ? String(lastEntry.note || "") : "";
    if (assignmentGuid) {
      respondToRoleInvitation(assignmentGuid, "decline", declineNote || undefined)
        .then(() => console.info("[workflowSideEffects] Role invitation declined:", assignmentGuid))
        .catch((err) => console.warn("[workflowSideEffects] Role invitation decline failed:", err?.message || err));
    }
    return { ok: true };
  }

  // DEV-1088. Mini-Project role-assignment invitation (Decline).
  // Mirror of the accept branch above. The respond endpoint flips the
  // assignment to terminal 'declined' (no team roster side effect) and
  // notifies the inviter.
  if (w.action_type === "mini_project.role_assignment") {
    const assignmentGuid = String((w.payload as any)?.assignment_guid || "");
    const hist = Array.isArray(w.history) ? w.history : [];
    const lastEntry = hist.length > 0 ? hist[hist.length - 1] : null;
    const declineNote = lastEntry?.action === "reject" ? String(lastEntry.note || "") : "";
    if (assignmentGuid) {
      // Decline doesn't touch the team roster — only the role chip
      // moves from pending → gone, so we dispatch the roles_changed
      // event but skip team_persisted.
      import("../api/miniProjectRoles")
        .then(({ respondToMiniProjectRoleInvitation }) =>
          respondToMiniProjectRoleInvitation(assignmentGuid, {
            action: "decline",
            note: declineNote || null,
          })
        )
        .then(() => {
          console.info("[workflowSideEffects] Mini-Project role invitation declined:", assignmentGuid);
          window.dispatchEvent(new Event("l4e_miniproject_roles_changed"));
        })
        .catch((err) => console.warn("[workflowSideEffects] Mini-Project role invitation decline failed:", err?.message || err));
    }
    return { ok: true };
  }

  // DEV-1193 Phase 4. Slot-anchored community role invitation (Decline).
  // Backend flips the assignment to 'declined' (terminal). No slot
  // mutation (slot was never occupied by this invitee yet).
  if (w.action_type === "community.slot_role_assignment") {
    const assignmentGuid = String((w.payload as any)?.assignment_guid || "");
    const hist = Array.isArray(w.history) ? w.history : [];
    const lastEntry = hist.length > 0 ? hist[hist.length - 1] : null;
    const declineNote = lastEntry?.action === "reject" ? String(lastEntry.note || "") : "";
    if (assignmentGuid) {
      import("../api/communityRoleSlotInvitations")
        .then(({ respondToCommunityRoleSlotInvitation }) =>
          respondToCommunityRoleSlotInvitation(assignmentGuid, {
            action: "decline",
            note: declineNote || null,
          }),
        )
        .then(() =>
          console.info("[workflowSideEffects] Community slot invitation declined:", assignmentGuid),
        )
        .catch((err) =>
          console.warn(
            "[workflowSideEffects] Community slot invitation decline failed:",
            err?.message || err,
          ),
        );
    }
    return { ok: true };
  }

  // DEV-1414 — org membership admission (Decline / Reject).
  // Request declined by an admin, or invitation declined by the invitee.
  // The pending/invited membership row goes dormant (status 'inactive',
  // ended_at set server-side) — retained for audit, never deleted.
  if (
    w.action_type === "community.membership_request" ||
    w.action_type === "community.membership_invitation"
  ) {
    const userGuid = String((w.payload as any)?.user_guid || "");
    const communityGuid = String((w.payload as any)?.community_guid || w.target_guid || "");
    const membershipType = String((w.payload as any)?.membership_type || "org_member");
    if (userGuid && communityGuid) {
      apiUpsertCommunityMembership({
        userGuid,
        communityEntityGuid: communityGuid,
        membershipType,
        status: "inactive",
      })
        .then(() => {
          window.dispatchEvent(
            new CustomEvent("l4e_security_updated", { detail: { source: "membership-admission" } }),
          );
        })
        .catch((err) =>
          console.warn("[workflowSideEffects] Membership decline upsert failed:", err?.message || err),
        );
    }
    return { ok: true };
  }

  // DEV-892. Community role-assignment invitation (Decline).
  // Recipient declined — flip the pending grant to declined. Row stays for
  // audit; assigner can see it was offered and rejected.
  if (w.action_type === "community.role_assignment") {
    const grantGuid = String((w.payload as any)?.grant_guid || "");
    if (grantGuid) {
      import("../security/v2/apiClient")
        .then(({ permissionsApiV2 }) => permissionsApiV2.declineGrant(grantGuid))
        .then(() => console.info("[workflowSideEffects] Community role grant declined:", grantGuid))
        .catch((err) => console.warn("[workflowSideEffects] Community role grant decline failed:", err?.message || err));
    }
    return { ok: true };
  }

  if (w.action_type === "project.kpi_progress_update") {
    const entryGuid = String((w.payload as any)?.entry_guid || "");
    if (!entryGuid) return { ok: false, error: "Missing entry_guid in workflow payload." };
    setKpiProgressEntryStatus({ entry_guid: entryGuid, status: "rejected", by_user_guid: String(rejectedBy || "") });
  }

  // ── Rejected escalated verification ──
  // Assignment reverts from pending_escalated_review back to active.
  // Backend handles the actual status transition via API callback.
  if (w.action_type === "project.role_completion_verification") {
    console.info(
      "[workflowSideEffects] Role completion verification REJECTED:",
      (w.payload as any)?.assignment_guid,
      "by", rejectedBy,
      "— assignment reverts to active"
    );
    // Increment disputes_count on the PM's trust record (backend handles this)
  }

  // ── Rejected PM trust promotion ──
  if (w.action_type === "community.pm_trust_promotion") {
    console.info(
      "[workflowSideEffects] PM trust promotion REJECTED:",
      (w.payload as any)?.pm_user_guid,
      "community:", (w.payload as any)?.community_guid,
      "by", rejectedBy
    );
  }

  return { ok: true };
}

export function applyCancelledWorkflowSideEffects(w: WorkflowRequest, cancelledBy: string): ApplySideEffectsResult {
  if (!w || w.status !== "cancelled") return { ok: true };

  // Emit notifications to requestor and resolver
  emitResolutionNotifications(w, cancelledBy, "cancelled");
  if (w.action_type === "project.kpi_progress_update") {
    const entryGuid = String((w.payload as any)?.entry_guid || "");
    if (!entryGuid) return { ok: false, error: "Missing entry_guid in workflow payload." };
    setKpiProgressEntryStatus({ entry_guid: entryGuid, status: "cancelled", by_user_guid: String(cancelledBy || "") });
  }
  return { ok: true };
}
